/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Button, Container, MenuItem, Stack, TextField, Typography } from '@mui/material';

import API from '../data/api';
import { isAuthorizedRole } from '../utils/authUtil';
import { FinanceView } from '../components/dashboard/views/FinanceView';
import { ExecutiveView } from '../components/dashboard/views/ExecutiveView';
import { OperationsView } from '../components/dashboard/views/OperationsView';
import { CustomsView } from '../components/dashboard/views/CustomsView';
import InvoiceList from '../components/ui/InvoiceList';
import { LogisticsView } from 'src/components/dashboard/views/LogisticsView';
import { SalesView } from 'src/components/dashboard/views/SalesView';
import DepotExecutiveView from 'src/components/dashboard/views/DepotExecutiveView';
import { CustomSelect } from 'src/components/custom/CustomSelect';
import { isNullValue } from 'src/utils/formUtil';

const initFilter = {
  depot: { value: '', required: false, error: false, errorMessage: 'Please select a depot' },
};

export default function DashboardAppPage() {
  const isCustomsUser = isAuthorizedRole(['CUSTOMS-L1']);
  const isDepotExecutiveUser = isAuthorizedRole(['DEPOT-EXECUTIVE-C1']);
  const hasDashTable = !isAuthorizedRole(['OPERATIONS-C1', 'CUSTOMS-L1', 'SALES-C2', 'DEPOT-EXECUTIVE-C1']);

  const [cardsData, setCardsData] = useState([]);
  const [productSegmentation, setProductSegmentation] = useState([]);
  const [productDateFilter, setProductDateFilter] = useState('current_month');
  const [regressionTracking, setRegressionTracking] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filter, setFilter] = useState({ ...initFilter });
  const [depots, setDepots] = useState([]);
  const [cashVarianceSummary, setCashVarianceSummary] = useState(null);
  const [cashVarianceTrend, setCashVarianceTrend] = useState(null);
  const [pumpVarianceTrend, setPumpVarianceTrend] = useState(null);
  const [profitabilitySummary, setProfitabilitySummary] = useState(null);
  const [profitabilityComparison, setProfitabilityComparison] = useState(null);
  const [fuelSalesControlTrend, setFuelSalesControlTrend] = useState(null);
  const [fuelSalesControlRevenueByFuelType, setFuelSalesControlRevenueByFuelType] = useState(null);
  const [fuelSalesControlVolumeShare, setFuelSalesControlVolumeShare] = useState(null);
  const [dailyStatisticsSummary, setDailyStatisticsSummary] = useState(null);
  const [dailyStatisticsTrend, setDailyStatisticsTrend] = useState(null);
  const [cashierExceptionSummary, setCashierExceptionSummary] = useState(null);
  const [cashierExceptionTrend, setCashierExceptionTrend] = useState(null);
  const [depotBatches, setDepotBatches] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [cashVarianceBatchBreakdown, setCashVarianceBatchBreakdown] = useState(null);

  useEffect(() => {
    getDepots();
  }, [isDepotExecutiveUser]);

  useEffect(() => {
    if (isDepotExecutiveUser) {
      return;
    }
    fetchCardData();
    fetchProductSegmentData();
    fetchRegressionTrackingData();
  }, [productDateFilter, startDate, endDate, isDepotExecutiveUser]);

  useEffect(() => {
    if (!isDepotExecutiveUser || depots.length === 0 || filter.depot.value) {
      return;
    }
    handleInputChange('depot', depots[0].depot_id);
  }, [depots, filter.depot.value, isDepotExecutiveUser]);

  useEffect(() => {
    if (!isDepotExecutiveUser || !filter.depot.value) {
      return;
    }
    fetchDepotCashVarianceSummary();
    fetchDepotCashVarianceTrend();
    fetchDepotPumpVarianceTrend();
    fetchDepotProfitabilitySummary();
    fetchDepotProfitabilityComparison();
    fetchDepotFuelSalesControlRevenueByFuelType();
    fetchDepotFuelSalesControlTrend();
    fetchDepotFuelSalesControlVolumeShare();
    fetchDepotDailyStatisticsSummary();
    fetchDepotDailyStatisticsTrend();
    fetchDepotCashierExceptionSummary();
    fetchDepotCashierExceptionTrend();
    fetchDepotBatches();
  }, [isDepotExecutiveUser, filter.depot.value, startDate, endDate]);

  useEffect(() => {
    if (!isDepotExecutiveUser || !selectedBatchId) {
      return;
    }
    fetchDepotCashVarianceBatchBreakdown();
  }, [isDepotExecutiveUser, selectedBatchId, startDate, endDate, filter.depot.value]);

  const buildAnalyticsQuery = () => {
    const params = new URLSearchParams();
    if (productDateFilter) {
      params.append('date_filter', productDateFilter);
    }
    if (startDate) {
      params.append('time_start', startDate);
    }
    if (endDate) {
      params.append('time_end', endDate);
    }
    return params.toString();
  };

  const fetchRegressionTrackingData = async () => {
    let regression = {};
    try {
      const query = buildAnalyticsQuery();
      const response = await API.get(`analytics/regression_tracking${query ? `?${query}` : ''}`);
      const data = response.data?.graph?.data || [];
      data?.forEach((item) => {
        regression = { ...regression, ...item };
      });
      setRegressionTracking(regression);
    } catch (error) {
      console.error('Error fetching regression tracking data:', error);
    }
  };

  const fetchCardData = async () => {
    try {
      const query = buildAnalyticsQuery();
      const cardResponse = await API.get(`analytics/cards${query ? `?${query}` : ''}`);
      setCardsData(cardResponse.data.cards?.data);
    } catch (error) {
      console.error('Error fetching card data:', error);
    }
  };

  const fetchProductSegmentData = async () => {
    try {
      const query = buildAnalyticsQuery();
      const response = await API.get(`analytics/product_segmentation${query ? `?${query}` : ''}`);
      setProductSegmentation(response.data.product_segmentation);
    } catch (error) {
      console.error('Error fetching product segmentation data:', error);
    }
  };

  const fetchDepotCashVarianceSummary = async () => {
    try {
      const params = new URLSearchParams();
      params.append('depot_id', filter.depot.value);
      if (startDate) {
        params.append('time_start', startDate);
      }
      if (endDate) {
        params.append('time_end', endDate);
      }

      const response = await API.get(`depots/cash-variance/summary?${params.toString()}`);
      setCashVarianceSummary(response?.data?.cards || null);
    } catch (error) {
      console.error('Error fetching cash variance summary: ', error);
      setCashVarianceSummary(null);
    }
  };

  const fetchDepotCashVarianceTrend = async () => {
    try {
      const params = new URLSearchParams();
      params.append('depot_id', filter.depot.value);
      if (startDate) {
        params.append('time_start', startDate);
      }
      if (endDate) {
        params.append('time_end', endDate);
      }

      const response = await API.get(`depots/cash-variance/trend?${params.toString()}`);
      setCashVarianceTrend(response?.data || null);
    } catch (error) {
      console.error('Error fetching cash variance trend: ', error);
      setCashVarianceTrend(null);
    }
  };

  const fetchDepotPumpVarianceTrend = async () => {
    try {
      const params = new URLSearchParams();
      params.append('depot_id', filter.depot.value);
      if (startDate) {
        params.append('time_start', startDate);
      }
      if (endDate) {
        params.append('time_end', endDate);
      }

      const response = await API.get(`depots/pump-variance/trend?${params.toString()}`);
      setPumpVarianceTrend(response?.data || null);
    } catch (error) {
      console.error('Error fetching pump variance trend: ', error);
      setPumpVarianceTrend(null);
    }
  };

  const fetchDepotProfitabilitySummary = async () => {
    try {
      const params = new URLSearchParams();
      params.append('depot_id', filter.depot.value);
      if (startDate) {
        params.append('time_start', startDate);
      }
      if (endDate) {
        params.append('time_end', endDate);
      }

      const response = await API.get(`depots/profitability/summary?${params.toString()}`);
      setProfitabilitySummary(response?.data?.cards || null);
    } catch (error) {
      console.error('Error fetching profitability summary: ', error);
      setProfitabilitySummary(null);
    }
  };

  const fetchDepotProfitabilityComparison = async () => {
    try {
      const params = new URLSearchParams();
      params.append('depot_id', filter.depot.value);
      if (startDate) {
        params.append('time_start', startDate);
      }
      if (endDate) {
        params.append('time_end', endDate);
      }

      const response = await API.get(`depots/profitability/comparison?${params.toString()}`);
      setProfitabilityComparison(response?.data || null);
    } catch (error) {
      console.error('Error fetching profitability comparison: ', error);
      setProfitabilityComparison(null);
    }
  };

  const fetchDepotFuelSalesControlTrend = async () => {
    try {
      const params = new URLSearchParams();
      params.append('chart', 'true');
      params.append('depot_id', filter.depot.value);
      if (startDate) {
        params.append('time_start', startDate);
      }
      if (endDate) {
        params.append('time_end', endDate);
      }

      const response = await API.get(`depots/fuel-sales-control/trend?${params.toString()}`);
      setFuelSalesControlTrend(response?.data || null);
    } catch (error) {
      console.error('Error fetching fuel sales control trend: ', error);
      setFuelSalesControlTrend(null);
    }
  };

  const fetchDepotFuelSalesControlRevenueByFuelType = async () => {
    try {
      const params = new URLSearchParams();
      params.append('depot_id', filter.depot.value);
      if (startDate) {
        params.append('time_start', startDate);
      }
      if (endDate) {
        params.append('time_end', endDate);
      }

      const response = await API.get(`depots/fuel-sales-control/revenue-by-fuel-type?${params.toString()}`);
      setFuelSalesControlRevenueByFuelType(response?.data || null);
    } catch (error) {
      console.error('Error fetching fuel sales control revenue by fuel type: ', error);
      setFuelSalesControlRevenueByFuelType(null);
    }
  };

  const fetchDepotFuelSalesControlVolumeShare = async () => {
    try {
      const params = new URLSearchParams();
      params.append('depot_id', filter.depot.value);
      if (startDate) {
        params.append('time_start', startDate);
      }
      if (endDate) {
        params.append('time_end', endDate);
      }

      const response = await API.get(`depots/fuel-sales-control/volume-share?${params.toString()}`);
      setFuelSalesControlVolumeShare(response?.data || null);
    } catch (error) {
      console.error('Error fetching fuel sales control volume share: ', error);
      setFuelSalesControlVolumeShare(null);
    }
  };

  const fetchDepotDailyStatisticsTrend = async () => {
    try {
      const params = new URLSearchParams();
      params.append('depot_id', filter.depot.value);
      if (startDate) {
        params.append('time_start', startDate);
      }
      if (endDate) {
        params.append('time_end', endDate);
      }

      const response = await API.get(`depots/daily-statistics/trend?${params.toString()}`);
      setDailyStatisticsTrend(response?.data || null);
    } catch (error) {
      console.error('Error fetching daily statistics trend: ', error);
      setDailyStatisticsTrend(null);
    }
  };

  const fetchDepotDailyStatisticsSummary = async () => {
    try {
      const params = new URLSearchParams();
      params.append('depot_id', filter.depot.value);
      if (startDate) {
        params.append('time_start', startDate);
      }
      if (endDate) {
        params.append('time_end', endDate);
      }

      const response = await API.get(`depots/daily-statistics/summary?${params.toString()}`);
      setDailyStatisticsSummary(response?.data?.cards || null);
    } catch (error) {
      console.error('Error fetching daily statistics summary: ', error);
      setDailyStatisticsSummary(null);
    }
  };

  const fetchDepotCashierExceptionTrend = async () => {
    try {
      const params = new URLSearchParams();
      params.append('depot_id', filter.depot.value);
      params.append('page', '1');
      params.append('records', '500');
      if (startDate) {
        params.append('time_start', startDate);
      }
      if (endDate) {
        params.append('time_end', endDate);
      }

      const response = await API.get(`depots/cashier-exception/trend?${params.toString()}`);
      setCashierExceptionTrend(response?.data || null);
    } catch (error) {
      console.error('Error fetching cashier exception trend: ', error);
      setCashierExceptionTrend(null);
    }
  };

  const fetchDepotCashierExceptionSummary = async () => {
    try {
      const params = new URLSearchParams();
      params.append('depot_id', filter.depot.value);
      if (startDate) {
        params.append('time_start', startDate);
      }
      if (endDate) {
        params.append('time_end', endDate);
      }

      const response = await API.get(`depots/cashier-exception/summary?${params.toString()}`);
      setCashierExceptionSummary(response?.data?.cards || null);
    } catch (error) {
      console.error('Error fetching cashier exception summary: ', error);
      setCashierExceptionSummary(null);
    }
  };

  const fetchDepotBatches = async () => {
    try {
      const params = new URLSearchParams();
      params.append('depot_id', filter.depot.value);
      if (startDate) {
        params.append('time_start', startDate);
      }
      if (endDate) {
        params.append('time_end', endDate);
      }
      const response = await API.get(`depots/batches?${params.toString()}`);
      const batches = response?.data?.batches || [];
      setDepotBatches(batches);
      setSelectedBatchId((current) => {
        const currentExists = batches.some((batch) => String(batch.id) === String(current));
        if (currentExists) {
          return current;
        }
        return batches.length > 0 ? String(batches[0].id) : '';
      });
      if (batches.length === 0) {
        setCashVarianceBatchBreakdown(null);
      }
    } catch (error) {
      console.error('Error fetching depot batches: ', error);
      setDepotBatches([]);
      setSelectedBatchId('');
      setCashVarianceBatchBreakdown(null);
    }
  };

  const fetchDepotCashVarianceBatchBreakdown = async () => {
    try {
      const params = new URLSearchParams();
      params.append('batch_id', selectedBatchId);
      if (startDate) {
        params.append('time_start', startDate);
      }
      if (endDate) {
        params.append('time_end', endDate);
      }

      const response = await API.get(`depots/cash-variance/batch-breakdown?${params.toString()}`);
      setCashVarianceBatchBreakdown(response?.data || null);
    } catch (error) {
      console.error('Error fetching cash variance batch breakdown: ', error);
      setCashVarianceBatchBreakdown(null);
    }
  };

  const getDepots = async () => {
    let depotList = [];
    try {
      const endPoint = isDepotExecutiveUser
        ? 'depots/depots'
        : 'inventory/backoffice/depots?depot_type=storage';
      const response = await API.get(endPoint);

      if (response.status === 200) {
        const depotsData = Array.isArray(response.data)
          ? response.data
          : response.data?.depots || response.data?.data || [];
        depotList = depotsData.map((depot) => ({
          ...depot,
          depot_id: depot?.depot_id || depot?.id,
          depot_name: depot?.depot_name || depot?.name,
        }));
      } else {
        console.error('Error fetching depots: ', response.status);
      }
    } catch (error) {
      console.error('Error fetching depots: ', error);
    }
    setDepots(depotList);
  };

  const handleInputChange = (field, value, error = false, message = null) => {
    const nextFilter = {
      ...filter,
      [field]: {
        ...filter[field],
        value,
        error: (isNullValue(value) && filter[field].required) || error,
        errorMessage: message || filter[field].errorMessage,
      },
    };
    setFilter(nextFilter);
  };

  const handleDepotSelection = (depotId) => {
    handleInputChange('depot', depotId, isNullValue(depotId));
  };

  const clearDateRange = () => {
    setStartDate('');
    setEndDate('');
  };

  if (isDepotExecutiveUser) {
    return (
      <>
        <Helmet>
          <title> Dashboard </title>
        </Helmet>

        <Container maxWidth={false} disableGutters sx={{ pb: 4 }}>
          <DepotExecutiveView
            depots={depots}
            selectedDepotId={filter.depot.value}
            onDepotChange={handleDepotSelection}
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onClearDates={clearDateRange}
            cashVarianceSummary={cashVarianceSummary}
            cashVarianceTrend={cashVarianceTrend}
            pumpVarianceTrend={pumpVarianceTrend}
            profitabilitySummary={profitabilitySummary}
            profitabilityComparison={profitabilityComparison}
            fuelSalesControlRevenueByFuelType={fuelSalesControlRevenueByFuelType}
            fuelSalesControlTrend={fuelSalesControlTrend}
            fuelSalesControlVolumeShare={fuelSalesControlVolumeShare}
            dailyStatisticsSummary={dailyStatisticsSummary}
            dailyStatisticsTrend={dailyStatisticsTrend}
            cashierExceptionSummary={cashierExceptionSummary}
            cashierExceptionTrend={cashierExceptionTrend}
            depotBatches={depotBatches}
            selectedBatchId={selectedBatchId}
            onBatchChange={setSelectedBatchId}
            cashVarianceBatchBreakdown={cashVarianceBatchBreakdown}
          />
        </Container>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title> Dashboard </title>
      </Helmet>

      <Container maxWidth={false}>
        <Box sx={{ display: 'flex', borderRadius: 1, mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ width: '100%' }}>
            <Typography variant="h4" sx={{ mb: 1 }}>
              {isCustomsUser ? 'SubOrder' : 'Financial'} Summary View
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ minWidth: { xs: '100%', md: 430 } }}>
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: startDate || undefined }}
              fullWidth
            />
            <Button variant="outlined" onClick={clearDateRange}>
              Clear
            </Button>
          </Stack>

          {isAuthorizedRole(['CUSTOMS-L1temp']) && (
            <Box sx={{ display: 'flex', flexShrink: 0, mr: 2, minWidth: 200 }}>
              <CustomSelect
                fullWidth
                labelId="depot-select-label"
                label="Depot"
                value={filter?.depot.value}
                onChange={(event) => handleDepotSelection(event.target.value)}
                required={filter?.depot.required}
                error={filter?.depot.error}
                helperText={filter?.depot.error && filter?.depot.errorMessage}
              >
                <MenuItem value="" disabled>
                  <em>Select Depot</em>
                </MenuItem>
                {depots?.map((depot) => (
                  <MenuItem key={`depot-order-${depot.depot_id}`} value={depot.depot_id}>
                    {depot.depot_name}
                  </MenuItem>
                ))}
              </CustomSelect>
            </Box>
          )}
        </Box>

        {isAuthorizedRole(['EXECUTIVE-C1']) && (
          <ExecutiveView
            cardsData={cardsData || []}
            productData={productSegmentation}
            regressionData={regressionTracking}
            periodFilter={productDateFilter}
            setPeriodFilter={setProductDateFilter}
          />
        )}

        {isAuthorizedRole(['FINANCE-C2', 'FINANCE-L1', 'DEMO']) && (
          <FinanceView
            cardsData={cardsData}
            productData={productSegmentation}
            regressionData={regressionTracking}
            periodFilter={productDateFilter}
            setPeriodFilter={setProductDateFilter}
          />
        )}

        {isAuthorizedRole(['OPERATIONS-C2', 'OPERATIONS-L1']) && (
          <OperationsView
            cardsData={cardsData}
            productData={productSegmentation}
            periodFilter={productDateFilter}
            setPeriodFilter={setProductDateFilter}
          />
        )}

        {isAuthorizedRole(['LOGISTICS-C2', 'LOGISTICS-L1']) && (
          <LogisticsView
            cardsData={cardsData || []}
            productData={productSegmentation}
            regressionData={regressionTracking}
            periodFilter={productDateFilter}
            setPeriodFilter={setProductDateFilter}
          />
        )}

        {isAuthorizedRole(['CUSTOMS-L1']) && (
          <CustomsView
            cardsData={cardsData || []}
            productData={productSegmentation}
            regressionData={regressionTracking}
            periodFilter={productDateFilter}
            setPeriodFilter={setProductDateFilter}
          />
        )}

        {isAuthorizedRole(['SALES-C2']) && (
          <SalesView
            cardsData={cardsData || []}
            productData={productSegmentation}
            regressionData={regressionTracking}
            periodFilter={productDateFilter}
            setPeriodFilter={setProductDateFilter}
          />
        )}

        {hasDashTable && (
          <>
            <Typography variant="h5" sx={{ my: 2 }}>
              Recent Invoices
            </Typography>
            <InvoiceList />
          </>
        )}
      </Container>
    </>
  );
}
